import React from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@stvgroup/stv-ui-components'
import SearchCard from './SearchCard'

/**
 * Displays a grid of search results or a loading skeleton.
 * @param {Array} results - The array of search result objects from the API.
 * @param {boolean} loading - Indicates if the search is in a loading state.
 * @param {boolean} usePoster - Determines whether to display cards with a poster aspect ratio.
 */
const SearchResults = ({ results, loading, usePoster }) => {
  /**
   * Safely parses a JSON string from the search result's customImage field
   * to extract a specific image rendition URL.
   * @param {string} customImage - The raw JSON string for the custom image.
   * @returns {string} The image URL or an empty string if parsing fails or the rendition
   * doesn't exist.
   */
  const getPosterImage = (images) => {
    const image = images.find(img => img.imageType === 'PPMB')
    if (image?._filepath) {
      return image ? image._filepath.replace('[width]', '300').replace('[height]', '400') : ''
    }
    return `https://images.stv.tv/player/master/${image?.filename}`
  }

  const getRelativeUrl = (absoluteUrl) => {
    if (!absoluteUrl) return ''
    try {
      const url = new URL(absoluteUrl)
      return url.pathname
    } catch (error) {
      return absoluteUrl
    }
  }
  if (loading) {
    return (
      <Grid container spacing={{ xs: 1, sm: 2 }}>
        {Array.from(new Array(12)).map((_, index) => (
          // Using array index as a key is acceptable here as the list is static
          // during the loading state.
          // eslint-disable-next-line react/no-array-index-key
          <Grid key={index} size={{ xs: usePoster ? 4 : 6, md: usePoster ? 3 : 4 }}>
            <SearchCard loading usePoster={usePoster} />
          </Grid>
        ))}
      </Grid>
    )
  }

  if (results.length === 0) {
    return <p>No results found</p>
  }

  return (
    <Grid container spacing={{ xs: 1, sm: 2 }}>
      {results.map(result => (
        <Grid key={result.id.raw} size={{ xs: usePoster ? 4 : 6, md: usePoster ? 3 : 4 }}>
          <SearchCard
            title={result.title}
            image={getPosterImage(result.images)}
            url={getRelativeUrl(result.permalink)}
            usePoster={usePoster}
            showOverlay={!usePoster}
          />
        </Grid>
      ))}
    </Grid>
  )
}

SearchResults.propTypes = {
  /**
   * Array of result objects from the search API.
   */
  results: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      images: PropTypes.arrayOf(PropTypes.shape({
        imageType: PropTypes.string,
        _filepath: PropTypes.string,
      })),
      permalink: PropTypes.string,
    }),
  ),
  /**
   * Flag to indicate that a search is in progress.
   */
  loading: PropTypes.bool,
  /**
   * Flag to determine the aspect ratio of the result cards.
   */
  usePoster: PropTypes.bool,
}

SearchResults.defaultProps = {
  results: [],
  loading: false,
  usePoster: false,
}

export default SearchResults
